import { $isMarkNode, $unwrapMarkNode, MarkNode } from '@lexical/mark';
import {
  $createRangeSelection,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  ElementNode,
  LexicalNode,
  PointType,
  RangeSelection,
  TextNode,
} from 'lexical';
import { IMap } from 'typings/DeepIMap';
import { TextAbbreviationDocuments } from 'models';
import { cleanUpText } from 'utils/html';

type GetAbbreviationNode = {
  abbreviationId?: string;
  abbreviationTerm?: string;
};

export const $getAbbreviationSelectionNode = (selection: RangeSelection): MarkNode | null | undefined => {
  const nodes = selection.getNodes();
  const abbreviationNode = nodes.length > 1
    ? nodes.find($isMarkNode)
    : nodes[0].getParents().find($isMarkNode);

  return abbreviationNode && $isMarkNode(abbreviationNode)
    ? abbreviationNode
    : undefined;
};

function $getSelectedSlice(node: TextNode, selection: RangeSelection): string {
  const text = node.getTextContent();

  let startKey = selection.anchor.getNode().getKey();
  let startOffset = selection.anchor.offset;
  let endKey = selection.focus.getNode().getKey();
  let endOffset = selection.focus.offset;

  if (selection.isBackward()) {
    [startKey, endKey] = [endKey, startKey];
    [startOffset, endOffset] = [endOffset, startOffset];
  }

  const key = node.getKey();

  if (startKey === endKey && key === startKey) {
    return text.slice(startOffset, endOffset);
  }

  if (key === startKey) {
    return text.slice(startOffset);
  }

  if (key === endKey) {
    return text.slice(0, endOffset);
  }

  return text;
}

function $nodesArrayToHtmlWithSubSup(nodes: LexicalNode[], selection: RangeSelection | null = null): string {
  const parts: string[] = [];

  for (const node of nodes) {
    if ($isTextNode(node)) {
      const text = selection ? $getSelectedSlice(node, selection) : node.getTextContent();

      if (!text) {
        continue;
      }

      const sanitizedText = cleanUpText(text);
      if (node.hasFormat?.('subscript')) {
        parts.push(`<sub>${sanitizedText}</sub>`);
      } else if (node.hasFormat?.('superscript')) {
        parts.push(`<sup>${sanitizedText}</sup>`);
      } else {
        parts.push(sanitizedText);
      }
    }
  }

  return parts.join('');
}

export const $getAbbreviationData = (): GetAbbreviationNode => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return {};
  }

  const node = $getAbbreviationSelectionNode(selection);

  if (!node) {
    return {
      abbreviationId: undefined,
      abbreviationTerm: $nodesArrayToHtmlWithSubSup(selection.getNodes(), selection),
    };
  }

  const [abbreviationId] = node.getIDs();

  return {
    abbreviationId,
    abbreviationTerm: $nodesArrayToHtmlWithSubSup(node.getChildren()),
  };
};

export const $selectAbbreviation = (abbreviationId: string, abbreviationNumber = 1): boolean => {
  const selection = $createRangeSelection();
  const nodes = $getRoot().getAllTextNodes();
  if (!nodes) {
    return false;
  }

  const abbreviations = nodes.filter((node) => {
    const parent = node.getParents().find($isMarkNode);

    if (!parent) {
      return false;
    }

    return parent.getIDs()[0] === abbreviationId;
  });

  const target = abbreviations[abbreviationNumber - 1];

  if (!target) {
    return false;
  }

  selection.focus.set(target.getKey(), 0, 'text');
  selection.anchor.set(target.getKey(), target.getTextContentSize(), 'text');
  $setSelection(selection);

  return true;
};

export const $removeNodeAbbreviation = (node: MarkNode): void => {
  const [abbreviationId] = node.getIDs();
  node.deleteID(abbreviationId);
  $unwrapMarkNode(node);
};

export const $removeAbbreviationIfTermChanged = (markNode: MarkNode, abbreviationsData: IMap<TextAbbreviationDocuments>): void => {
  const [abbreviationId] = markNode.getIDs();
  const abbreviationTerm = $nodesArrayToHtmlWithSubSup(markNode.getChildren());
  const termBeforeEdit = abbreviationsData.getIn([abbreviationId, 'abbreviation', 'term'], '');
  if ((termBeforeEdit as string).toLowerCase() !== abbreviationTerm.toLowerCase()) {
    $removeNodeAbbreviation(markNode);
  }
};

function findNonEmptyCharIndex(text: string, offset: number, isForward: boolean): number {
  const step = isForward ? 1 : -1;
  let index = offset;
  while (index >= 0 && index < text.length) {
    if (text[index].trim() !== '') {
      return index;
    }
    index += step;
  }

  return -1;
}

function $findNonWhitespaceTextNode(point: PointType, direction: 'forward' | 'backward'): { textNode: TextNode | ElementNode; offset: number } {
  let currentNode: TextNode | ElementNode | null = point.getNode();
  const isForward = direction === 'forward';
  let offset = isForward ? point.offset : point.offset - 1;
  while (currentNode) {
    const textContent = currentNode.getTextContent();
    offset = findNonEmptyCharIndex(textContent, offset, isForward);
    if (offset !== -1) {
      const resultOffset = isForward ? offset : offset + 1;

      return { offset: resultOffset, textNode: currentNode };
    }
    currentNode = isForward ? currentNode.getNextSibling() : currentNode.getPreviousSibling();
    if (!currentNode) {
      break;
    }
    offset = isForward ? 0 : currentNode.getTextContent().length - 1;
  }

  return { offset: point.offset, textNode: point.getNode() };
}

export function $trimSelection(selection: RangeSelection): void {
  const textWithWhiteSpaces = selection.getTextContent();
  const trimmedText = textWithWhiteSpaces.trim();
  if (textWithWhiteSpaces.length === trimmedText.length) {
    return;
  }
  const anchor = selection.anchor;
  const focus = selection.focus;
  const isBackward = selection.isBackward();
  const [startPoint, endPoint] = isBackward ? [focus, anchor] : [anchor, focus];
  const { offset: startOffset, textNode: startTextNode } = $findNonWhitespaceTextNode(startPoint, 'forward');
  const { offset: endOffset, textNode: endTextNode } = $findNonWhitespaceTextNode(endPoint, 'backward');

  if ($isTextNode(startTextNode) && $isTextNode(endTextNode)) {
    selection.setTextNodeRange(startTextNode, startOffset, endTextNode, endOffset);
  }
}
